import { reportErrorToServer } from '../api/client';

const MAX_REPORTS_PER_PAGE_LOAD = 5;
const MAX_STACK_CHARS = 2000;

let reportsSent = 0;
let teardown: (() => void) | null = null;

function extractMessage(reason: unknown, fallback: string): string {
    if (reason instanceof Error) {
        return reason.message;
    }
    if (typeof reason === 'string' && reason.length > 0) {
        return reason;
    }

    return fallback;
}

function extractStack(reason: unknown): string | undefined {
    if (reason instanceof Error && typeof reason.stack === 'string') {
        return reason.stack.slice(0, MAX_STACK_CHARS);
    }

    return undefined;
}

function report(endpoint: string, message: string, stack: string | undefined): void {
    // Hard cap per page load - a broken render loop must not flood the log endpoint.
    if (reportsSent >= MAX_REPORTS_PER_PAGE_LOAD) {
        return;
    }
    reportsSent += 1;

    reportErrorToServer({
        endpoint,
        status: 0,
        errorData: null,
        context: {
            message,
            stack,
            occurredAt: new Date().toISOString(),
        },
    });
}

function handleWindowError(event: ErrorEvent): void {
    const reason: unknown = event.error;
    report(
        'window:onerror',
        event.message || extractMessage(reason, 'Uncaught error'),
        extractStack(reason),
    );
}

function handleUnhandledRejection(event: PromiseRejectionEvent): void {
    const reason: unknown = event.reason;
    report(
        'window:unhandledrejection',
        extractMessage(reason, 'Unhandled promise rejection'),
        extractStack(reason),
    );
}

/**
 * Registers window-level handlers so uncaught errors and rejections reach the server.
 * Safe to call more than once. Returns a teardown that removes the handlers.
 */
export function installGlobalErrorReporting(): () => void {
    if (teardown) {
        return teardown;
    }

    window.addEventListener('error', handleWindowError);
    window.addEventListener('unhandledrejection', handleUnhandledRejection);

    teardown = () => {
        window.removeEventListener('error', handleWindowError);
        window.removeEventListener('unhandledrejection', handleUnhandledRejection);
        teardown = null;
    };

    return teardown;
}
